import { useState, type FormEvent } from 'react';
import { messages } from '@fruitways/shared';
import { Banner, Button, PasswordField, TextField } from '@/components';
import { ApiRequestError, NetworkError } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

export function LoginScreen() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [failed, setFailed] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const u = username.trim();
    setUsernameError(u ? '' : messages.login.usernameRequired);
    setPasswordError(password ? '' : messages.login.passwordRequired);
    setFailed('');
    if (!u || !password) return;
    setBusy(true);
    try {
      await login(u, password, remember);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'invalid_credentials') {
        setFailed(messages.login.failed);
      } else if (err instanceof NetworkError) {
        setFailed(messages.queue.sendFailed);
      } else {
        setFailed(messages.errors.generic);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fw-frame">
      <form
        onSubmit={submit}
        noValidate
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-surface)',
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 'var(--space-6)',
            padding: 'var(--space-6) var(--screen-padding-x)',
          }}
        >
          <img
            src="/fruitways-logo.png"
            alt="Fruitways"
            style={{ width: '168px', height: 'auto', alignSelf: 'center' }}
          />

          {failed && (
            <Banner tone="error" rounded>
              {failed}
            </Banner>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--field-gap)' }}>
            <TextField
              label={messages.login.username}
              value={username}
              onChange={(v) => {
                setUsername(v);
                setUsernameError('');
              }}
              error={usernameError}
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              name="username"
              required
            />
            <PasswordField
              label={messages.login.password}
              value={password}
              onChange={(v) => {
                setPassword(v);
                setPasswordError('');
              }}
              error={passwordError}
              showLabel="Show password"
              hideLabel="Hide password"
            />
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                minHeight: '48px',
                font: 'var(--type-body)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={{ width: '22px', height: '22px', accentColor: 'var(--color-primary)' }}
              />
              {messages.login.remember}
            </label>
          </div>

          {/* No self-service reset: an admin sets a password in the user and farm file. */}
          <Button type="submit" loading={busy}>
            {messages.login.logIn}
          </Button>
        </div>
      </form>
    </div>
  );
}
